import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { Aviso, Cargando, FilaHecha, INPUT_CLASS, LABEL_CLASS } from "./ui";
import type { PasoProps } from "./types";

/**
 * PASO 2 (restaurantes): la sala y sus mesas.
 *
 * Comprobado antes de escribir el texto: un restaurante SIN mesas montadas
 * sigue aceptando reservas (`public/availability.ts` cae a modo aforo cuando no
 * hay mesas). Así que esto no desbloquea el widget; lo que da es que cada
 * reserva se siente sola en su mesa y se vea el servicio de un vistazo. El
 * texto lo cuenta así, sin asustar.
 *
 * Crea la sala y las mesas de golpe: se dice cuántas mesas hay de cada tamaño y
 * el asistente las coloca en el lienzo en filas. Mover las mesas para que el
 * mapa se parezca al local es cosa del editor de mapa (`/tables`), que es donde
 * tiene sentido arrastrar.
 */

interface MesaResumen {
  id: string;
  name: string;
}
interface SalaResumen {
  id: string;
  name: string;
  tables?: MesaResumen[];
}

/** Tamaños con los que se monta un comedor normal. */
const TAMANOS = [2, 4, 6, 8] as const;
/** Celdas del lienzo por lado (el editor de mapa usa 8/12/16/20). */
const LIENZO = 12;
/** Mesas por fila al colocarlas solas. */
const POR_FILA = 5;

export default function PasoMesas({
  refrescar,
  registrarAvance,
  registrarTextoAvance,
}: PasoProps) {
  const { showToast } = useGlobalToast();
  const [salas, setSalas] = useState<SalaResumen[] | null>(null);
  // La primera sala viene rellena (Salón, 4+4 mesas) para que un local
  // pequeño la monte de un clic. A partir de la segunda el formulario se
  // vacía: Luis lo vio con «Salón» y las mismas mesas otra vez y no sabía si
  // era otra sala o la misma (6-oct-2026).
  const [nombreSala, setNombreSala] = useState("Salón");
  const [cantidades, setCantidades] = useState<Record<number, string>>({
    2: "4",
    4: "4",
    6: "",
    8: "",
  });
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await apiClient.get("/tables/rooms", {
        params: { includeInactive: "false" },
      });
      const rooms: SalaResumen[] = res.data?.rooms ?? [];
      setSalas(rooms);
      if (rooms.length > 0) {
        setNombreSala("");
        setCantidades({ 2: "", 4: "", 6: "", 8: "" });
      }
    } catch {
      setSalas([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    // Nunca bloquea: sin mapa de mesas se sigue reservando por aforo.
    registrarAvance(null);
    registrarTextoAvance(
      (salas?.length ?? 0) > 0 ? null : "Lo haré más tarde, seguir",
    );
    return () => registrarTextoAvance(null);
  }, [salas, registrarAvance, registrarTextoAvance]);

  const totalMesas = TAMANOS.reduce(
    (suma, tamano) => suma + (parseInt(cantidades[tamano] || "0", 10) || 0),
    0,
  );

  const crear = async () => {
    const nombre = nombreSala.trim();
    if (!nombre) {
      showToast("Ponle nombre a la sala (Salón, Terraza…)", "error");
      return;
    }
    if (totalMesas === 0) {
      showToast("Di cuántas mesas tienes de cada tamaño", "error");
      return;
    }
    if (totalMesas > 60) {
      showToast(
        "Son muchas mesas de golpe: monta hasta 60 aquí y el resto en el mapa",
        "error",
      );
      return;
    }

    try {
      setCreando(true);
      const salaRes = await apiClient.post("/tables/rooms", {
        name: nombre,
        gridSize: LIENZO,
        isActive: true,
      });
      const roomId: string | undefined = salaRes.data?.room?.id;
      if (!roomId) {
        showToast("No se pudo crear la sala", "error");
        return;
      }

      // Se crean en serie a propósito: son pocas y así, si una falla (un nombre
      // repetido, por ejemplo), las anteriores ya están guardadas y se ve dónde
      // se ha parado.
      let indice = 0;
      let falladas = 0;
      for (const tamano of TAMANOS) {
        const cuantas = parseInt(cantidades[tamano] || "0", 10) || 0;
        for (let i = 0; i < cuantas; i++) {
          const columna = indice % POR_FILA;
          const fila = Math.floor(indice / POR_FILA);
          try {
            await apiClient.post("/tables", {
              roomId,
              name: `Mesa ${indice + 1}`,
              minCapacity: 1,
              maxCapacity: tamano,
              shape: tamano <= 2 ? "square" : "rectangle",
              positionX: (columna * 2) % LIENZO,
              positionY: (fila * 2) % LIENZO,
              isActive: true,
            });
          } catch {
            falladas += 1;
          }
          indice += 1;
        }
      }

      await cargar();
      refrescar();
      if (falladas > 0) {
        showToast(
          `Sala creada con ${indice - falladas} de ${indice} mesas. Las que falten las puedes añadir en el mapa.`,
          "error",
        );
      } else {
        showToast(`${nombre} montada con ${indice} mesas`);
      }
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo crear la sala"), "error");
    } finally {
      setCreando(false);
    }
  };

  if (!salas) return <Cargando texto="Cargando tus salas…" />;

  return (
    <div className="space-y-4">
      <Aviso>
        Con tus mesas dadas de alta, cada reserva se sienta sola en la que le
        toca y ves el servicio de un vistazo. Tus clientes ya pueden reservar sin
        esto, pero entonces tendrás que colocarlos tú a mano.
      </Aviso>

      {salas.length > 0 && (
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 px-3">
          {salas.map((s) => (
            <FilaHecha
              key={s.id}
              titulo={s.name}
              detalle={`${s.tables?.length ?? 0} mesa${
                (s.tables?.length ?? 0) === 1 ? "" : "s"
              }`}
            />
          ))}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
        <p className="text-sm font-medium text-gray-900">
          {salas.length === 0 ? "Monta tu sala" : "¿Tienes otra sala? Añádela aquí"}
        </p>
        {salas.length > 0 && (
          <p className="text-xs text-gray-500 -mt-2">
            Si ya están todas, pulsa Continuar.
          </p>
        )}
        <div>
          <label className={LABEL_CLASS} htmlFor="wizard-sala-nombre">
            Nombre de la sala
          </label>
          <input
            id="wizard-sala-nombre"
            className={INPUT_CLASS}
            value={nombreSala}
            onChange={(e) => setNombreSala(e.target.value)}
            placeholder={salas.length === 0 ? "Salón" : "Terraza, Comedor, Barra…"}
            maxLength={100}
          />
        </div>
        <div>
          <p className={LABEL_CLASS}>¿Cuántas mesas tienes de cada tamaño?</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TAMANOS.map((tamano) => (
              <div key={tamano}>
                <label
                  className="text-xs text-gray-500"
                  htmlFor={`wizard-mesas-${tamano}`}
                >
                  De {tamano} personas
                </label>
                <input
                  id={`wizard-mesas-${tamano}`}
                  className={INPUT_CLASS}
                  value={cantidades[tamano]}
                  onChange={(e) =>
                    setCantidades((prev) => ({
                      ...prev,
                      [tamano]: e.target.value.replace(/[^0-9]/g, "").slice(0, 2),
                    }))
                  }
                  inputMode="numeric"
                  placeholder="0"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Se crearán {totalMesas} mesa{totalMesas === 1 ? "" : "s"} numeradas.
            Después podrás colocarlas como están en tu local desde el mapa de
            mesas.
          </p>
        </div>
        <button
          type="button"
          onClick={crear}
          disabled={creando}
          className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {creando ? "Creando mesas…" : "Crear sala y mesas"}
        </button>
      </div>
    </div>
  );
}
